import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Shield, Wrench, UserCog, Edit2,
  Mail, User, Key, KeyRound, Briefcase, Users, X, Megaphone, Printer, Save, HelpCircle, Settings2, Film
} from 'lucide-react';
import { useAppSetting, useUpdateAppSetting } from '@/hooks/useAppSettings';
import { HelpTooltip } from '@/components/HelpTooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { apiPost } from '@/lib/apiFetch';
import { debounce } from '@/lib/utils';
import { toast } from 'sonner';

import type { UserRole } from '@/lib/roles';

interface AppUser {
  id: string;
  user_id: string | null;
  name: string;
  surname: string;
  initials: string;
  alias: string;
  email: string | null;
  role: UserRole;
  is_active: boolean;
  status: string;
  created_at: string;
  managed_by: string | null;
  location_ids: string[];
  can_manage_playlists: boolean;
}

interface Manager {
  id: string;
  name: string;
  initials: string;
}

const ROLE_CONFIG: Record<UserRole, { label: string; color: string; icon: any }> = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800', icon: UserCog },
  manager: { label: 'Manager', color: 'bg-orange-100 text-orange-800', icon: Briefcase },
  // 'management' is in the database ENUM and in every cost-visibility check,
  // but was missing here - so those users fell through the `|| technician`
  // fallback below and were badged as Technicians.
  management: { label: 'Management', color: 'bg-amber-100 text-amber-800', icon: Briefcase },
  technician: { label: 'Technician', color: 'bg-blue-100 text-blue-800', icon: Wrench },
  marketing: { label: 'Marketing', color: 'bg-green-100 text-green-800', icon: Megaphone },
  retail: { label: 'Retail', color: 'bg-teal-100 text-teal-800', icon: Megaphone },
  retail_shop: { label: 'Retail Shop', color: 'bg-cyan-100 text-cyan-800', icon: Users },
};

export function AdminPage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    initials: '',
    alias: '',
    role: 'technician' as UserRole,
    is_active: true,
    managed_by: '' as string,
    location_ids: [] as string[],
    can_manage_playlists: false,
  });
  const [formError, setFormError] = useState('');

  // Credentials dialog state (for adding login to existing user)
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [userForCredentials, setUserForCredentials] = useState<AppUser | null>(null);
  const [credentialsData, setCredentialsData] = useState({
    email: '',
    password: '',
  });
  const [credentialsError, setCredentialsError] = useState('');

  // Password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userForReset, setUserForReset] = useState<AppUser | null>(null);
  const [resetData, setResetData] = useState({
    newEmail: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [resetError, setResetError] = useState('');

  const queryClient = useQueryClient();

  // Fetch users from technicians table
  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users', search, roleFilter, showInactive],
    queryFn: async () => {
      let query = supabase
        .from('technicians')
        .select(`
          *,
          manager:technicians!managed_by(name, initials)
        `)
        .order('name');

      if (search) {
        query = query.or(`name.ilike.%${search}%,surname.ilike.%${search}%,alias.ilike.%${search}%,initials.ilike.%${search}%`);
      }

      if (roleFilter) {
        query = query.eq('role', roleFilter);
      }

      if (!showInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (AppUser & { manager?: { name: string; initials: string } })[];
    },
  });

  // Fetch locations for retail shop assignment
  const { data: locations = [] } = useQuery({
    queryKey: ['demo-locations'],
    queryFn: async () => {
      const { data } = await supabase.from('demo_locations').select('id, name').eq('is_active', true).order('sort_order');
      return data ?? [];
    },
  });

  // Fetch managers for dropdown
  const { data: managers } = useQuery({
    queryKey: ['managers-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name, initials')
        .eq('role', 'manager')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return data as Manager[];
    },
  });

  // Fetch role counts
  const { data: roleCounts } = useQuery({
    queryKey: ['admin-role-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technicians')
        .select('role, is_active');
      
      if (error) throw error;
      
      const counts: Record<string, number> = {
        super_admin: 0,
        admin: 0,
        manager: 0,
        technician: 0,
        total_active: 0,
      };
      
      data?.forEach(user => {
        if (user.is_active) {
          counts.total_active++;
          const role = user.role || 'technician';
          if (role in counts) {
            counts[role]++;
          }
        }
      });
      
      return counts;
    },
  });

  // Printer assignments
  const { data: printerAssignments, isLoading: printersLoading } = useQuery({
    queryKey: ['printer-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('printer_assignments')
        .select('*');
      if (error) throw error;
      return data as { id: string; technician_id: string; label_printer_type: string; label_printer_name: string | null; label_printer_ip: string | null; certificate_printer_type: string; certificate_printer_name: string | null }[];
    },
  });

  // Eligible users for printer assignment (technicians + managers)
  const { data: printerUsers } = useQuery({
    queryKey: ['printer-eligible-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name, initials, role')
        .in('role', ['super_admin', 'admin', 'technician', 'manager'])
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string; initials: string; role: string }[];
    },
  });

  // QZ Tray discovered printers
  const [discoveredPrinters, setDiscoveredPrinters] = useState<string[]>([]);
  const [discovering, setDiscovering] = useState(false);

  const discoverPrinters = async () => {
    setDiscovering(true);
    try {
      const { listPrinters } = await import('@/lib/qzPrinter');
      const printers = await listPrinters();
      setDiscoveredPrinters(printers);
      toast.success(`Found ${printers.length} printer${printers.length !== 1 ? 's' : ''}`);
    } catch (e: any) {
      toast.error(e.message?.includes('Unable to connect')
        ? 'QZ Tray not running — install it from qz.io and start it'
        : e.message || 'Failed to discover printers');
    } finally {
      setDiscovering(false);
    }
  };

  const [printerEdits, setPrinterEdits] = useState<Record<string, { label_printer_type: string; label_printer_name: string; label_printer_ip: string; certificate_printer_type: string; certificate_printer_name: string }>>({});

  const getPrinterConfig = (userId: string) => {
    if (printerEdits[userId]) return printerEdits[userId];
    const existing = printerAssignments?.find(a => a.technician_id === userId);
    return {
      label_printer_type: existing?.label_printer_type || 'zebra',
      label_printer_name: existing?.label_printer_name || '',
      label_printer_ip: existing?.label_printer_ip || '',
      certificate_printer_type: existing?.certificate_printer_type || 'brother_103x164',
      certificate_printer_name: existing?.certificate_printer_name || '',
    };
  };

  const updatePrinterEdit = (userId: string, field: string, value: string) => {
    setPrinterEdits(prev => ({
      ...prev,
      [userId]: { ...getPrinterConfig(userId), [field]: value },
    }));
  };

  const savePrinterAssignment = useMutation({
    mutationFn: async (userId: string) => {
      const config = getPrinterConfig(userId);
      const { error } = await supabase
        .from('printer_assignments')
        .upsert({
          technician_id: userId,
          label_printer_type: config.label_printer_type,
          label_printer_name: config.label_printer_name || null,
          label_printer_ip: config.label_printer_type === 'zebra' ? config.label_printer_ip || null : null,
          certificate_printer_type: config.certificate_printer_type,
          certificate_printer_name: config.certificate_printer_name || null,
        }, { onConflict: 'technician_id' });
      if (error) throw error;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['printer-assignments'] });
      setPrinterEdits(prev => { const n = { ...prev }; delete n[userId]; return n; });
      toast.success('Printer assignment saved');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to save printer assignment'),
  });

  // Create user mutation (technician record only - no auth)
  const createUser = useMutation({
    mutationFn: async (userData: typeof formData) => {
      // Create technician record only
      const { error: dbError } = await supabase
        .from('technicians')
        .insert({
          name: userData.name,
          surname: userData.surname,
          initials: userData.initials.toUpperCase(),
          alias: userData.alias || userData.name,
          // null rather than '' - the partial unique index would treat two
          // blank strings as a collision, but ignores NULLs.
          email: userData.email.trim() || null,
          role: userData.role,
          is_active: userData.is_active,
          status: userData.is_active ? 'active' : 'inactive',
          managed_by: userData.managed_by || null,
          location_ids: (userData.role === 'retail' || userData.role === 'retail_shop') ? userData.location_ids : [],
          can_manage_playlists: userData.role === 'technician' && userData.can_manage_playlists,
        });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-role-counts'] });
      queryClient.invalidateQueries({ queryKey: ['technicians'] });
      closeDialog();
      toast.success('User created successfully. Use "Add Login" to create credentials.');
    },
    onError: (error: any) => {
      setFormError(error.message || 'Failed to create user');
    },
  });

  // Update user mutation
  const updateUser = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AppUser> }) => {
      const { error } = await supabase
        .from('technicians')
        .update({
          ...updates,
          status: updates.is_active ? 'active' : 'inactive',
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-role-counts'] });
      queryClient.invalidateQueries({ queryKey: ['technicians'] });
      closeDialog();
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      setFormError(error.message || 'Failed to update user');
    },
  });

  // Add credentials mutation - uses admin API if available
  const addCredentials = useMutation({
    mutationFn: async ({ technicianId, email, password }: { technicianId: string; email: string; password: string }) => {
      // Creates the auth user with a confirmed email and links it to the
      // technician record - all server-side in api/admin-users.ts.
      await apiPost('/api/admin-users', {
        action: 'create-login',
        technicianId,
        email,
        password,
      });
      return { needsConfirmation: false };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setCredentialsDialogOpen(false);
      setUserForCredentials(null);
      setCredentialsData({ email: '', password: '' });
      setCredentialsError('');
      
      if (data.needsConfirmation) {
        toast.success('Credentials created! User needs to confirm email before logging in.');
      } else {
        toast.success('Login credentials created successfully');
      }
    },
    onError: (error: any) => {
      setCredentialsError(error.message || 'Failed to create credentials');
    },
  });

  // Reset credentials mutation - uses admin API
  const resetCredentials = useMutation({
    mutationFn: async ({ userId, newEmail, newPassword }: { userId: string; newEmail?: string; newPassword?: string }) => {
      if (!newEmail && !newPassword) {
        throw new Error('No changes to make');
      }

      await apiPost('/api/admin-users', {
        action: 'update-login',
        userId,
        email: newEmail,
        password: newPassword,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setResetDialogOpen(false);
      setUserForReset(null);
      setResetData({ newEmail: '', newPassword: '', confirmPassword: '' });
      setResetError('');
      toast.success('Credentials updated successfully');
    },
    onError: (error: any) => {
      setResetError(error.message || 'Failed to update credentials');
    },
  });

  const handleSearchChange = debounce((value: string) => {
    setSearch(value);
  }, 300);

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      surname: '',
      initials: '',
      alias: '',
      role: 'technician',
      is_active: true,
      managed_by: '',
      location_ids: [],
      can_manage_playlists: false,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (user: AppUser) => {
    setEditingUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      name: user.name,
      surname: user.surname,
      initials: user.initials,
      alias: user.alias,
      role: user.role || 'technician',
      is_active: user.is_active,
      managed_by: user.managed_by || '',
      location_ids: (user as any).location_ids ?? [],
      can_manage_playlists: user.can_manage_playlists ?? false,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openCredentialsDialog = (user: AppUser) => {
    setUserForCredentials(user);
    setCredentialsData({ email: '', password: '' });
    setCredentialsError('');
    setCredentialsDialogOpen(true);
  };

  const openResetDialog = async (user: AppUser) => {
    if (!user.user_id) {
      toast.error('User has no login credentials');
      return;
    }
    
    setUserForReset(user);
    setResetData({ newEmail: '', newPassword: '', confirmPassword: '' });
    setResetError('');
    setResetDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormError('');
  };

  const handleSubmit = () => {
    setFormError('');

    // Validation
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.surname.trim()) {
      setFormError('Surname is required');
      return;
    }
    if (!formData.initials.trim()) {
      setFormError('Initials are required');
      return;
    }

    if (editingUser) {
      updateUser.mutate({
        id: editingUser.id,
        updates: {
          name: formData.name,
          surname: formData.surname,
          initials: formData.initials.toUpperCase(),
          alias: formData.alias || formData.name,
          email: formData.email.trim() || null,
          role: formData.role,
          is_active: formData.is_active,
          managed_by: formData.managed_by || null,
          location_ids: (formData.role === 'retail' || formData.role === 'retail_shop') ? formData.location_ids : [],
          // Cleared on a move to another role, where it would mean nothing.
          can_manage_playlists: formData.role === 'technician' && formData.can_manage_playlists,
        },
      });
    } else {
      createUser.mutate(formData);
    }
  };

  const handleAddCredentials = () => {
    setCredentialsError('');

    if (!credentialsData.email.trim()) {
      setCredentialsError('Email is required');
      return;
    }
    if (!credentialsData.password || credentialsData.password.length < 8) {
      setCredentialsError('Password must be at least 8 characters');
      return;
    }

    if (userForCredentials) {
      addCredentials.mutate({
        technicianId: userForCredentials.id,
        email: credentialsData.email,
        password: credentialsData.password,
      });
    }
  };

  const handleResetCredentials = () => {
    setResetError('');

    if (!resetData.newEmail && !resetData.newPassword) {
      setResetError('Please enter a new email or password');
      return;
    }

    if (resetData.newPassword && resetData.newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (resetData.newPassword && resetData.newPassword !== resetData.confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    if (userForReset?.user_id) {
      resetCredentials.mutate({
        userId: userForReset.user_id,
        newEmail: resetData.newEmail || undefined,
        newPassword: resetData.newPassword || undefined,
      });
    }
  };

  const generateInitials = (name: string, surname: string): string => {
    return `${name[0] || ''}${surname[0] || ''}`.toUpperCase();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage user accounts and permissions
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          New User
        </Button>
      </div>

      {/* Role Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100">
                <Users className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{roleCounts?.total_active || 0}</p>
                <p className="text-xs text-muted-foreground">Total Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {(['super_admin', 'admin', 'manager', 'technician', 'marketing', 'retail', 'retail_shop'] as const).map((role) => {
          const config = ROLE_CONFIG[role];
          const Icon = config.icon;
          const count = roleCounts?.[role] || 0;
          
          return (
            <Card 
              key={role}
              className={`cursor-pointer transition-all ${roleFilter === role ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
              onClick={() => setRoleFilter(roleFilter === role ? '' : role)}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{config.label}s</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, alias, or initials..."
                className="pl-9 pr-9"
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); handleSearchChange(e.target.value); }}
              />
              {searchInput && (
                <button onClick={() => { setSearchInput(''); setSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="technician">Technician</option>
              <option value="marketing">Marketing</option>
              <option value="retail">Retail</option>
            </select>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-input"
              />
              Show inactive
            </label>

            {(roleFilter || showInactive) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setRoleFilter(''); setShowInactive(false); }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Initials</th>
                    <th className="text-left p-4 font-medium">Role</th>
                    <th className="text-left p-4 font-medium">Manager</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Login</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map((user) => {
                    const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.technician;
                    
                    return (
                      <tr key={user.id} className={`border-b hover:bg-muted/50 transition-colors ${!user.is_active ? 'opacity-50' : ''}`}>
                        <td className="p-4">
                          <div>
                            <span className="font-medium">{user.name} {user.surname}</span>
                            {user.alias !== user.name && (
                              <span className="text-sm text-muted-foreground ml-2">({user.alias})</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-sm font-medium">{user.initials}</span>
                        </td>
                        <td className="p-4">
                          <Badge className={roleConfig.color}>
                            {roleConfig.label}
                          </Badge>
                          {user.role === 'technician' && user.can_manage_playlists && (
                            <Badge variant="outline" className="ml-1.5 gap-1" title="Has Video Playlists access">
                              <Film className="h-3 w-3" />
                              Playlists
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          {user.manager ? (
                            <span className="text-sm">{user.manager.name}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          {user.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                          )}
                        </td>
                        <td className="p-4">
                          {user.user_id ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-blue-100 text-blue-800">Enabled</Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openResetDialog(user)}
                                title="Send password reset email"
                                className="h-6 w-6 p-0"
                              >
                                <KeyRound className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openCredentialsDialog(user)}
                              className="h-6 text-xs"
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Add Login
                            </Button>
                          )}
                        </td>
                        <td className="p-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(user)}
                            title="Edit user"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!users || users.length === 0) && (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <User className="h-8 w-8 mb-2" />
                  <p>No users found</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Printer Assignments */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              <h2 className="text-lg font-semibold">Printer Assignments</h2>
              <HelpTooltip tipKey="admin.printerAssignment" />
            </div>
            <Button variant="outline" size="sm" onClick={discoverPrinters} disabled={discovering}>
              <Search className="h-3 w-3 mr-1" />
              {discovering ? 'Discovering…' : 'Discover Printers'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            Assign label and certificate printers per user. Uses QZ Tray for silent printing — install from <a href="https://qz.io" target="_blank" rel="noopener noreferrer" className="underline">qz.io</a>.
          </p>
          {discoveredPrinters.length > 0 && (
            <p className="text-xs text-muted-foreground mb-4">
              Discovered: {discoveredPrinters.join(', ')}
            </p>
          )}
          {printersLoading ? (
            <div className="flex items-center justify-center h-24 text-muted-foreground">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Role</th>
                    <th className="pb-2 font-medium">Label Printer Type</th>
                    <th className="pb-2 font-medium">Label Printer</th>
                    <th className="pb-2 font-medium">Certificate Printer</th>
                    <th className="pb-2 font-medium w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {(printerUsers ?? []).map(user => {
                    const config = getPrinterConfig(user.id);
                    const hasEdits = !!printerEdits[user.id];
                    return (
                      <tr key={user.id} className="border-b last:border-0">
                        <td className="py-2 font-medium">{user.name}</td>
                        <td className="py-2">
                          <Badge variant="outline" className="text-xs capitalize">{user.role}</Badge>
                        </td>
                        <td className="py-2">
                          <select
                            value={config.label_printer_type}
                            onChange={e => updatePrinterEdit(user.id, 'label_printer_type', e.target.value)}
                            className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                          >
                            <option value="zebra">Zebra (Raw ZPL)</option>
                            <option value="brother_62mm">Brother 62mm (HTML)</option>
                          </select>
                        </td>
                        <td className="py-2">
                          {discoveredPrinters.length > 0 ? (
                            <select
                              value={config.label_printer_name}
                              onChange={e => updatePrinterEdit(user.id, 'label_printer_name', e.target.value)}
                              className="h-8 px-2 rounded-md border border-input bg-background text-sm min-w-[180px]"
                            >
                              <option value="">Select printer…</option>
                              {discoveredPrinters.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          ) : (
                            <Input
                              value={config.label_printer_name}
                              onChange={e => updatePrinterEdit(user.id, 'label_printer_name', e.target.value)}
                              placeholder="OS printer name"
                              className="h-8 w-48 text-sm"
                            />
                          )}
                        </td>
                        <td className="py-2">
                          {discoveredPrinters.length > 0 ? (
                            <select
                              value={config.certificate_printer_name}
                              onChange={e => updatePrinterEdit(user.id, 'certificate_printer_name', e.target.value)}
                              className="h-8 px-2 rounded-md border border-input bg-background text-sm min-w-[180px]"
                            >
                              <option value="">Select printer…</option>
                              {discoveredPrinters.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          ) : (
                            <Input
                              value={config.certificate_printer_name}
                              onChange={e => updatePrinterEdit(user.id, 'certificate_printer_name', e.target.value)}
                              placeholder="OS printer name"
                              className="h-8 w-48 text-sm"
                            />
                          )}
                        </td>
                        <td className="py-2">
                          {hasEdits && (
                            <Button
                              size="sm"
                              onClick={() => savePrinterAssignment.mutate(user.id)}
                              disabled={savePrinterAssignment.isPending}
                            >
                              <Save className="h-3 w-3 mr-1" />Save
                            </Button>
                          )}
                          {!hasEdits && printerAssignments?.find(a => a.technician_id === user.id) && (
                            <Badge variant="outline" className="text-xs text-green-600">Configured</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {(!printerUsers || printerUsers.length === 0) && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-muted-foreground">
                        No technicians or managers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* App Settings */}
      <AppSettingsCard />

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            <DialogDescription>
              {editingUser 
                ? 'Update user details below.' 
                : 'Create a user profile. Add login credentials separately.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {formError}
              </div>
            )}

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">First Name *</Label>
                <Input
                  id="name"
                  placeholder="John"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ 
                      ...formData, 
                      name,
                      initials: formData.initials || generateInitials(name, formData.surname),
                      alias: formData.alias || name
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surname">Surname *</Label>
                <Input
                  id="surname"
                  placeholder="Smith"
                  value={formData.surname}
                  onChange={(e) => {
                    const surname = e.target.value;
                    setFormData({ 
                      ...formData, 
                      surname,
                      initials: formData.initials || generateInitials(formData.name, surname)
                    });
                  }}
                />
              </div>
            </div>

            {/* Notification address. Held on the technician record rather than
                read back from the auth user, so someone onboarded before their
                first sign-in still receives assignment mail. */}
            <div className="space-y-2">
              <Label htmlFor="notify-email">Email</Label>
              <Input
                id="notify-email"
                type="email"
                placeholder="jsmith@intercomp.com.mt"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Where assignment notifications are sent. Separate from login credentials.
              </p>
            </div>

            {/* Initials & Alias */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="initials">Initials *</Label>
                <Input
                  id="initials"
                  placeholder="JS"
                  maxLength={4}
                  className="uppercase"
                  value={formData.initials}
                  onChange={(e) => setFormData({ ...formData, initials: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alias">Display Name</Label>
                <Input
                  id="alias"
                  placeholder="John S."
                  value={formData.alias}
                  onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                />
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <option value="technician">Technician</option>
                <option value="manager">Manager</option>
                <option value="marketing">Marketing</option>
                <option value="retail">Retail</option>
                <option value="retail_shop">Retail Shop</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <p className="text-xs text-muted-foreground">
                {formData.role === 'super_admin' && 'Full access including user management'}
                {formData.role === 'admin' && 'Full access except user management'}
                {formData.role === 'manager' && 'View all data including costs, manage technicians'}
                {formData.role === 'technician' && 'View and update assigned work only'}
                {formData.role === 'marketing' && 'Read-only access to builds with B2C display names'}
                {formData.role === 'retail' && 'Same as Marketing but without Web Details access'}
                {formData.role === 'retail_shop' && 'Demo Stations only — demo mode, reboot, shutdown, HUD/video toggle, telemetry, playlist'}
              </p>
            </div>

            {/* Manager (only for technicians) */}
            {formData.role === 'technician' && managers && managers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="managed_by">Managed By</Label>
                <select
                  id="managed_by"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.managed_by}
                  onChange={(e) => setFormData({ ...formData, managed_by: e.target.value })}
                >
                  <option value="">No manager assigned</option>
                  {managers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.initials})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Per-person page access (only for technicians) */}
            {formData.role === 'technician' && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <input
                  type="checkbox"
                  id="can_manage_playlists"
                  checked={formData.can_manage_playlists}
                  onChange={(e) => setFormData({ ...formData, can_manage_playlists: e.target.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <div>
                  <Label htmlFor="can_manage_playlists" className="font-medium">Video Playlists access</Label>
                  <p className="text-xs text-muted-foreground">
                    Upload videos and manage playlists and their station assignments
                  </p>
                </div>
              </div>
            )}

            {/* Shop assignment (retail / retail_shop) */}
            {(formData.role === 'retail' || formData.role === 'retail_shop') && locations.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Shops</Label>
                <div className="grid grid-cols-2 gap-2">
                  {locations.map(loc => (
                    <label key={loc.id} className="flex items-center gap-2 cursor-pointer text-sm p-2 rounded-md border hover:bg-muted transition-colors">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded"
                        checked={formData.location_ids.includes(loc.id)}
                        onChange={e => {
                          const ids = e.target.checked
                            ? [...formData.location_ids, loc.id]
                            : formData.location_ids.filter(id => id !== loc.id);
                          setFormData({ ...formData, location_ids: ids });
                        }}
                      />
                      {loc.name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Retail users can only see and manage stations in their assigned shops.</p>
              </div>
            )}

            {/* Active status (only for editing) */}
            {editingUser && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <div>
                  <Label htmlFor="is_active" className="font-medium">Active User</Label>
                  <p className="text-xs text-muted-foreground">
                    Inactive users cannot log in or be assigned to work
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={createUser.isPending || updateUser.isPending}
            >
              {(createUser.isPending || updateUser.isPending) 
                ? 'Saving...' 
                : editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credentials Dialog */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Login Credentials</DialogTitle>
            <DialogDescription>
              Create login credentials for {userForCredentials?.name} {userForCredentials?.surname}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {credentialsError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {credentialsError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cred-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cred-email"
                  type="email"
                  placeholder="user@example.com"
                  className="pl-9"
                  value={credentialsData.email}
                  onChange={(e) => setCredentialsData({ ...credentialsData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred-password">Password</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cred-password"
                  type="password"
                  placeholder="Minimum 8 characters"
                  className="pl-9"
                  value={credentialsData.password}
                  onChange={(e) => setCredentialsData({ ...credentialsData, password: e.target.value })}
                />
              </div>
            </div>

          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredentialsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCredentials}
              disabled={addCredentials.isPending}
            >
              {addCredentials.isPending ? 'Creating...' : 'Create Login'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Credentials Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Credentials</DialogTitle>
            <DialogDescription>
              Update login credentials for {userForReset?.name} {userForReset?.surname}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {resetError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {resetError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="reset-email">New Email (optional)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="Leave blank to keep current"
                  className="pl-9"
                  value={resetData.newEmail}
                  onChange={(e) => setResetData({ ...resetData, newEmail: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-password">New Password (optional)</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-password"
                  type="password"
                  placeholder="Minimum 8 characters"
                  className="pl-9"
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                />
              </div>
            </div>

            {resetData.newPassword && (
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    className="pl-9"
                    value={resetData.confirmPassword}
                    onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  />
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Leave fields blank to keep current values. Changes take effect immediately.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetCredentials}
              disabled={resetCredentials.isPending}
            >
              {resetCredentials.isPending ? 'Updating...' : 'Update Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── App Settings Card ─────────────────────────────────────────────────────────

function AppSettingsCard() {
  const { data: tooltipsEnabled, isLoading } = useAppSetting('tooltips_enabled');
  const { data: hudBgOpacity, isLoading: opLoading } = useAppSetting('hud_bg_opacity');
  const updateSetting = useUpdateAppSetting();
  const currentOpacity = typeof hudBgOpacity === 'number' ? hudBgOpacity : 0.92;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="h-5 w-5" />
          <h2 className="text-lg font-semibold">App Settings</h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b">
            <div>
              <p className="text-sm font-medium">HUD Background Opacity</p>
              <p className="text-xs text-muted-foreground">Global default for all stations. Stations can override individually.</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="range" min="10" max="100" step="10"
                className="w-28 h-1 accent-blue-500 cursor-pointer"
                disabled={opLoading || updateSetting.isPending}
                value={Math.round(currentOpacity * 100)}
                onChange={e => updateSetting.mutate({ key: 'hud_bg_opacity', value: parseInt(e.target.value) / 100 })} />
              <span className="text-sm tabular-nums w-8 text-right">{Math.round(currentOpacity * 100)}%</span>
            </div>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Contextual Help Tooltips</p>
                <p className="text-xs text-muted-foreground">Show help icons next to features throughout the app for all users.</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!tooltipsEnabled}
              disabled={isLoading || updateSetting.isPending}
              onClick={() => updateSetting.mutate({ key: 'tooltips_enabled', value: !tooltipsEnabled })}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                tooltipsEnabled ? 'bg-primary' : 'bg-muted'
              } ${(isLoading || updateSetting.isPending) ? 'opacity-50' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                  tooltipsEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
